import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useWebSocket } from '../../../context/WebSocketContext';
import { apiClient } from '../../../utils/apiClient';

export interface Notification {
  _id: string;
  message: string;
  status: 'unread' | 'read';
  createdAt: string;
  type: string;
  relatedEntityId?: string;
}

export type NotificationCategory = 'jurisprudences' | 'dossiers' | 'abonnements' | 'system' | null;

const useNotifications = () => {
  const { user } = useAuth();
  const { socket } = useWebSocket();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await apiClient(`/notifications/${user.id}/unread-count`);
      if (response.ok) {
        const data = await response.json();
        setTotalUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de notifications non lues:', error);
    }
  }, [user?.id]);

  const fetchNotifications = useCallback(async (page: number, category?: NotificationCategory) => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      let path = `/notifications/${user.id}?page=${page}&limit=10`;
      if (category) {
        path += `&category=${category}`;
      }
      const response = await apiClient(path);
      if (response.ok) {
        const data = await response.json();
        setNotifications(prevNotifications =>
          page === 1 ? data.notifications : [...prevNotifications, ...data.notifications]
        );
        setHasMore(data.currentPage < data.totalPages);
        setCurrentPage(data.currentPage);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const response = await apiClient(`/notifications/${notificationId}/read`, {
        method: 'PATCH',
      });

      if (response.ok) {
        setNotifications(prevNotifications =>
          prevNotifications.map(notification =>
            notification._id === notificationId
              ? { ...notification, status: 'read' }
              : notification
          )
        );
        setTotalUnreadCount(prevCount => Math.max(prevCount - 1, 0));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la notification comme lue:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async (category?: NotificationCategory) => {
    if (!user?.id) return;
    try {
      let path = `/notifications/${user.id}/mark-all-read`;
      if (category) {
        path += `?category=${category}`;
      }
      const response = await apiClient(path, {
        method: 'PATCH',
      });

      if (response.ok) {
        setNotifications(prevNotifications =>
          prevNotifications.map(notification => ({ ...notification, status: 'read' as const }))
        );
        setTotalUnreadCount(0);
      }
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && socket) {
      const handleNewNotification = (notificationData: Notification) => {
        setNotifications(prevNotifications => [notificationData, ...prevNotifications]);
        fetchUnreadCount();
        if (showNotifications) {
          fetchNotifications(1);
        }
      };

      socket.on('newNotification', handleNewNotification);

      return () => {
        socket.off('newNotification', handleNewNotification);
      };
    }
  }, [user?.id, socket, fetchNotifications, fetchUnreadCount, showNotifications]);

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchNotifications(currentPage + 1);
    }
  }, [hasMore, isLoading, currentPage, fetchNotifications]);

  // Effet pour charger les notifications initiales uniquement quand l'utilisateur est authentifié
  useEffect(() => {
    if (user?.id) {
      fetchUnreadCount();
    }
  }, [user?.id, fetchUnreadCount]);

  return {
    notifications,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    totalUnreadCount,
    showNotifications,
    setShowNotifications,
    fetchNotifications,
  };
};

export default useNotifications;
